import type { Socket } from "socket.io-client";
import { encodeArguments } from "./codec";
import {
  CHUNK_BYTES,
  MAX_MESSAGE_BYTES,
  TRANSFER_EVENT,
  type TransferReply,
  type TransportCallback,
} from "./protocol";

type Sender = Pick<Socket, "emit" | "timeout" | "connected" | "id">;
type Queue = { tail: Promise<void>; bytes: number };
const queues = new WeakMap<Sender, Queue>();

/** Existing callbacks retain their error-first timeout signature. The transport
 * never retries a logical message: the owning protocol retains its durable
 * queue / command ID, and decides whether an unknown outcome should be retried. */
export function emitBounded(
  socket: Sender,
  event: string,
  args: readonly unknown[],
  callback?: TransportCallback,
  timeoutMs = 10_000,
  expectAck = true,
): void {
  let bytes: Uint8Array;

  try {
    bytes = encodeArguments(args);

    if (bytes.length > MAX_MESSAGE_BYTES) {
      throw new Error("Message exceeds the 16 MiB transport limit");
    }
  } catch (error) {
    callback?.(
      error instanceof Error ? error : new Error("Cannot encode message"),
    );
    return;
  }

  const waiting = queues.get(socket);

  if (!waiting && bytes.length <= CHUNK_BYTES) {
    sendSmall(socket, event, args, callback, timeoutMs, expectAck);
    return;
  }

  const queue = waiting ?? { tail: Promise.resolve(), bytes: 0 };

  if (queue.bytes + bytes.length > MAX_MESSAGE_BYTES * 2) {
    callback?.(new Error("Socket send queue is full"));
    return;
  }

  queues.set(socket, queue);
  queue.bytes += bytes.length;
  const connectionId = socket.id;

  const run = async () => {
    try {
      if (!socket.connected || socket.id !== connectionId) {
        throw new Error("Socket connection changed");
      }

      if (bytes.length <= CHUNK_BYTES) {
        sendSmall(socket, event, args, callback, timeoutMs, expectAck);
        return;
      }

      const id = crypto.randomUUID();

      const request = async (frame: object): Promise<TransferReply> => {
        if (!socket.connected || socket.id !== connectionId) {
          throw new Error("Socket connection changed");
        }

        const reply: unknown = await new Promise((resolve, reject) => {
          socket
            .timeout(timeoutMs)
            .emit(
              TRANSFER_EVENT,
              frame,
              (error: Error | null, value: unknown) => {
                if (error) {
                  reject(error);
                } else {
                  resolve(value);
                }
              },
            );
        });

        if (
          !reply ||
          typeof reply !== "object" ||
          !("ok" in reply) ||
          reply.ok !== true
        ) {
          throw new Error("Large message transfer refused or unsupported");
        }

        return reply as TransferReply;
      };

      await request({
        kind: "begin",
        id,
        event,
        size: bytes.length,
        wantsAck: !!callback && expectAck,
      });
      let result: TransferReply = { ok: true };

      for (let offset = 0; offset < bytes.length; offset += CHUNK_BYTES) {
        result = await request({
          kind: "chunk",
          id,
          offset,
          bytes: bytes.subarray(offset, offset + CHUNK_BYTES),
        });
      }

      if (result.ok) {
        callback?.(null, ...(result.args ?? []));
      }
    } catch (error) {
      callback?.(error instanceof Error ? error : new Error("Transfer failed"));
    } finally {
      queue.bytes -= bytes.length;

      if (queue.bytes === 0 && queues.get(socket) === queue) {
        queues.delete(socket);
      }
    }
  };

  queue.tail = queue.tail.then(run);
}

function sendSmall(
  socket: Sender,
  event: string,
  args: readonly unknown[],
  callback: TransportCallback | undefined,
  timeoutMs: number,
  expectAck: boolean,
) {
  if (callback && expectAck) {
    socket.timeout(timeoutMs).emit(event, ...args, callback);
  } else {
    socket.emit(event, ...args);
    callback?.(null);
  }
}
